import { Controller } from "@hotwired/stimulus"
import { Sortable } from "sortablejs"

// Reorders the rows (CustomReportBlockEntry) of one custom report block.
//
// Rows are nested-attribute sub-forms, so nothing is sent on a move: each row's hidden
// position input is renumbered 1..n and travels with the report form. The form is
// renumbered once more on submit, which also numbers rows added since (nested-form,
// "Kolonne übernehmen", the timesheet prefill) without any of them knowing about it.
//
// One Sortable per block list. Blocks inside a group are separate lists side by side,
// never nested — SortableJS loses the drop of an item that holds another sortable.
export default class extends Controller {
  static targets = ["list", "handle", "positionInput"]
  // The data keys "Nach Datum sortieren" compares, in order (date, then start time).
  static values = { sortKeys: String }

  connect() {
    // An external table without a source or column mappings renders no rows.
    if (!this.hasListTarget) return

    this.sortable = new Sortable(this.listTarget, {
      draggable: ".nested-fields",
      handle: "[data-block-entry-sort-target='handle']",
      animation: 150,
      ghostClass: "opacity-40",
      onEnd: () => this.renumber(),
    })

    this.form = this.element.closest("form")
    this.renumberOnSubmit = () => this.renumber()
    this.form?.addEventListener("submit", this.renumberOnSubmit)
  }

  disconnect() {
    this.sortable?.destroy()
    this.form?.removeEventListener("submit", this.renumberOnSubmit)
  }

  sortByDate(event) {
    event?.preventDefault()
    const keys = this.sortKeysValue.split(",").filter(Boolean)
    if (keys.length === 0) return

    const rows = this.rows().map((row, index) => ({
      row,
      index,
      values: keys.map((key) => this.rowValue(row, key)),
    }))

    rows.sort((a, b) => this.compare(a, b))
    // Hidden (destroyed) rows keep their place relative to nothing; append them last.
    rows.forEach(({ row }) => this.listTarget.appendChild(row))
    this.destroyedRows().forEach((row) => this.listTarget.appendChild(row))
    this.keepLinksLast()
    this.renumber()
  }

  renumber() {
    let position = 0
    this.rows().forEach((row) => {
      const input = this.positionInputOf(row)
      if (!input) return

      position += 1
      input.value = position
    })
  }

  // ---- private ----

  // Blank values sort after filled ones; equal rows keep their current order.
  compare(a, b) {
    for (let i = 0; i < a.values.length; i++) {
      const left = a.values[i]
      const right = b.values[i]
      if (left === right) continue
      if (!left) return 1
      if (!right) return -1
      return left < right ? -1 : 1
    }
    return a.index - b.index
  }

  // Dates are ISO (DateFieldComponent's hidden field) and times "HH:MM", so plain
  // string comparison orders them.
  rowValue(row, key) {
    return row.querySelector(`[name$="[data][${key}]"]`)?.value?.trim() || ""
  }

  rows() {
    return this.listRows().filter((row) => !this.destroyed(row))
  }

  destroyedRows() {
    return this.listRows().filter((row) => this.destroyed(row))
  }

  listRows() {
    return Array.from(this.listTarget.children).filter((el) =>
      el.classList.contains("nested-fields"),
    )
  }

  destroyed(row) {
    return row.querySelector("input[name$='[_destroy]']")?.value === "1"
  }

  positionInputOf(row) {
    return row.querySelector("[data-block-entry-sort-target='positionInput']")
  }

  // A table block keeps its "add row" link inside the list, below the rows.
  keepLinksLast() {
    const links = this.listTarget.querySelector(":scope > [data-nested-form-target='links']")
    if (links) this.listTarget.appendChild(links)
  }
}
